import { iterateValidValues } from '../../../../helpers/SurveyUtils';
import { Interval, MetaTypes, PrescriptionJob } from '../../PrescriptionJob';
import PrescriptionModelReference, {
  Identifier,
} from '../../../../model/prescription/PrescriptionModelReference';
import {
  BulkPrescriptionJobs,
  LayerValueGrid,
  LayerValues,
  LayerValuesForFields,
  VRMForFields,
} from '../SeedingJobCommonTypes';
import { SURVEY_LAYERS } from '../../../../constants/SurveyLayers';
import { DirectPrescriptionJobStrategy } from '../../PrescriptionJobStrategy';
import {
  calculateIntervalAreas,
  calculateOverridesArea,
  initializeOverrides,
} from '../../PrescriptionUtils';

export class MultiLayerSeedingPrescriptionStrategyV1 extends DirectPrescriptionJobStrategy {
  miniatureMapSelection = [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.ALTITUDE];
  baseLayer = SURVEY_LAYERS.CLAY;

  createVRM = (layerValues: LayerValues, prescription: number): number[][] => {
    const humusValues = layerValues[SURVEY_LAYERS.HUMUS];
    const clayValues = layerValues[SURVEY_LAYERS.CLAY];
    const altitudeValues = layerValues[SURVEY_LAYERS.ALTITUDE];

    if (humusValues.length !== clayValues.length || humusValues.length !== altitudeValues.length) {
      throw new Error('All values for field must have the same length');
    }

    const seedingRateMap = new Array(humusValues.length)
      .fill(null)
      .map(() => new Array(humusValues[0].length).fill(null));
    const averageHeight = this.getAverageHeight(altitudeValues.flat());

    iterateValidValues(humusValues, (y: number, x: number, _) => {
      if (!humusValues[x][y]) {
        return;
      }

      const relativeAltitude = this.getRelativeAltitudeForPoint(
        altitudeValues[x][y],
        averageHeight
      );

      seedingRateMap[x][y] = this.seedingAmountForCoordinate(
        prescription,
        humusValues[x][y],
        clayValues[x][y],
        relativeAltitude
      );
    });

    return seedingRateMap;
  };

  createInitialIntervals = (seedingRateMap: LayerValueGrid | number[][][]): Interval[] => {
    const flattened: number[] = seedingRateMap.flat(Infinity).filter((value) => value !== null);
    let minValue = Infinity;
    let maxValue = -Infinity;
    for (const value of flattened) {
      minValue = Math.min(minValue, value);
      maxValue = Math.max(maxValue, value);
    }
    const firstStep: number = (3 * minValue + maxValue) / 4;
    const secondStep: number = (maxValue + minValue) / 2;
    const thirdStep: number = (3 * maxValue + minValue) / 4;

    return [
      new Interval(
        minValue,
        firstStep,
        this.getAveragePrescriptionForInterval(minValue, firstStep, flattened)
      ),
      new Interval(
        firstStep,
        secondStep,
        this.getAveragePrescriptionForInterval(firstStep, secondStep, flattened)
      ),
      new Interval(
        secondStep,
        thirdStep,
        this.getAveragePrescriptionForInterval(secondStep, thirdStep, flattened)
      ),
      new Interval(
        thirdStep,
        maxValue,
        this.getAveragePrescriptionForInterval(thirdStep, maxValue, flattened)
      ),
    ];
  };

  toReference = (): PrescriptionModelReference => {
    return new PrescriptionModelReference(Identifier.MultiLayerSeedingModel, 1);
  };

  isAdjustmentNeeded = () => {
    // For the seeding jobs, the user is inputting Desired seeding limit, and not a maximum.
    // As such, there it no upper limit that he cannot go over, and the button 'Adjustment
    // needed' is unnecessary.
    return false;
  };

  initializeBulkJobsWithoutOverview = (
    getJobName: (fieldId: number | string) => string,
    prescriptionJob: PrescriptionJob,
    bulkJobs: BulkPrescriptionJobs,
    vrms: VRMForFields,
    layerValuesForFields: LayerValuesForFields
  ): BulkPrescriptionJobs => {
    const updatedBulkJobs: BulkPrescriptionJobs = {};

    Object.keys(bulkJobs).forEach((fieldIdKey) => {
      const fieldId = Number(fieldIdKey);

      let updatedJob = { ...bulkJobs[fieldId] };
      const prescription =
        (prescriptionJob.maxPrescription / prescriptionJob.fieldSize) * updatedJob.fieldSize;

      updatedJob.forceRecalculated = false;
      updatedJob.values = vrms[fieldId];
      updatedJob.prescriptionModel = this.toReference();
      updatedJob.layerValues = layerValuesForFields[fieldId];
      updatedJob.totalPrescription = prescription;
      updatedJob.layer = SURVEY_LAYERS.CLAY;
      updatedJob.maxPrescription = prescription;
      updatedJob.seedingCalculatorParams = prescriptionJob.seedingCalculatorParams;
      updatedJob.jobName = getJobName(fieldId);
      updatedJob.metaType = MetaTypes.SEEDING;
      updatedJob.unit = prescriptionJob.unit;
      updatedJob.metering = prescriptionJob.metering;
      updatedJob.seasonId = prescriptionJob.seasonId;

      updatedJob.overrides = initializeOverrides(updatedJob);
      updatedJob.overrideAreas = calculateOverridesArea(updatedJob);
      updatedJob.intervals = this.createInitialIntervals(vrms[fieldId]);
      updatedJob = calculateIntervalAreas(updatedJob);

      updatedBulkJobs[fieldId] = updatedJob;
    });

    return updatedBulkJobs;
  };

  initializeSingleJob = (
    prescriptionJob: PrescriptionJob,
    vrm: LayerValueGrid,
    layerValues: LayerValues
  ): PrescriptionJob => {
    let singlePrescriptionJob: PrescriptionJob = { ...prescriptionJob };

    singlePrescriptionJob.prescriptionModel = this.toReference();
    singlePrescriptionJob.values = vrm;
    singlePrescriptionJob.layerValues = layerValues;
    singlePrescriptionJob.overrides = initializeOverrides(singlePrescriptionJob);
    singlePrescriptionJob.layer = SURVEY_LAYERS.CLAY;
    singlePrescriptionJob.overrideAreas = calculateOverridesArea(singlePrescriptionJob);
    singlePrescriptionJob.intervals = this.createInitialIntervals(vrm);
    singlePrescriptionJob = calculateIntervalAreas(singlePrescriptionJob);

    return singlePrescriptionJob;
  };

  private getAveragePrescriptionForInterval = (
    minValue: number,
    maxValue: number,
    allValues: number[]
  ): number => {
    const valuesInInterval = allValues.filter((value) => {
      return value >= minValue && value <= maxValue;
    });

    const sumOfValues = valuesInInterval.reduce((sum, value) => sum + value, 0);

    return sumOfValues / valuesInInterval.length;
  };

  seedingAmountForCoordinate = (
    prescription: number,
    humusPercentage: number,
    clayPercentage: number,
    relativeAltitude: number
  ): number => {
    return (
      this.adjustClayForPoint(clayPercentage) *
      this.adjustHumusForPoint(humusPercentage) *
      this.adjustAltitudeForPoint(relativeAltitude) *
      prescription
    );
  };

  private adjustClayForPoint = (clayPercentage: number): number => {
    let clayPercentageUsed = clayPercentage;

    if (clayPercentageUsed > 25) {
      clayPercentageUsed = 25;
    }

    return 0.015 * clayPercentageUsed + 0.86;
  };

  private adjustAltitudeForPoint = (relativeAltitude: number): number => {
    return (
      0.00009 * Math.pow(relativeAltitude, 3) -
      0.0000000000000004 * Math.pow(relativeAltitude, 2) +
      0.0072 * relativeAltitude +
      1
    );
  };

  private adjustHumusForPoint = (humusPercentage: number): number => {
    let usedHumusPercentage = humusPercentage;

    if (usedHumusPercentage > 25) {
      usedHumusPercentage = 25;
    } else if (usedHumusPercentage < 1.5) {
      usedHumusPercentage = 1.5;
    }

    return -0.12 * Math.log(usedHumusPercentage) + 1.13;
  };

  private getAverageHeight = (altitudeValues: Array<number | null>) => {
    const validAltitudes = altitudeValues.filter((value): value is number => value !== null);
    if (validAltitudes.length === 0) return 0;

    return validAltitudes.reduce((sum, value) => sum + value, 0) / validAltitudes.length;
  };

  private getRelativeAltitudeForPoint = (altitudeForPoint: number, averageHeight: number) => {
    const relativeAltitude = Math.abs(altitudeForPoint) - averageHeight;

    if (relativeAltitude < -12) {
      return -12;
    }

    if (relativeAltitude > 12) {
      return 12;
    }

    return relativeAltitude;
  };
}
